// composables/useGsapAnimation.ts
/**
 * A composable that wraps a GSAP animation timeline.
 *
 * @param target - The target element(s) to animate. Can be a single
 *   `HTMLElement`, a `Ref` to an `HTMLElement`, an array of `HTMLElement`s,
 *   or a `Ref` to an array of `HTMLElement`s.
 * @param setupFn - A function that sets up the GSAP timeline. This function
 *   will be called with the GSAP library as the first argument and the context
 *   object as the second argument. The function should return a GSAP timeline
 *   or `undefined` if no animation should be created.
 * @returns An object with a single property, `timeline`, which is a `Ref` to
 *   the created GSAP timeline.
 *
 * @example
 *   const element = ref<HTMLElement | null>(null)
 *   const wrapper = ref<HTMLElement | null>(null)
 *   const { timeline } = useGsapAnimation(wrapper, (gsap) => {
 *      const tl = gsap.timeline({ paused: true })
 *      tl.fromTo(element.value, { rotate: 360 }, { rotate: 0, duration: 1 })
 *      tl.play()
 *      return tl
 *  }, 'rotate-data')
 */
type GsapTarget = Element | Ref<Element | undefined>

export function useGsapAnimation(
  name: string,
  setupFn: (gsap: typeof globalThis.gsap, ctx: gsap.Context) => gsap.core.Timeline,
  target?: GsapTarget,
) {
  const { $gsap } = useNuxtApp()
  const animation = ref<AnimationItem>()

  const { setTimeline } = useAnimationState()

  let ctx: gsap.Context | null = null

  onMounted(() => {
    ctx = $gsap.context(() => {
      if (name) {
        animation.value = setTimeline(name, {
          tl: setupFn($gsap, ctx!),
          setup: () => setupFn($gsap, ctx!),
        })
      }
    }, unref(target))
  })

  onUnmounted(() => {
    ctx?.revert()
  })

  return animation
}
